/**
 * Words and addresses for campaigns (plan docs/plans/lms-plan.md 5.4, 8.1,
 * 8.2, 18.16; WP-43), shared by the Campaigns sub-view and the Assign wizard.
 */
import type { AssignmentStatus, CampaignKind, CampaignListItem, CampaignRecipient } from '../../../api/campaigns';
import { formatDay, Pill, roleLabel } from '../../../components/pickers/shared';
import type { PillTone } from '../../../components/pickers/shared';

/** The Campaigns sub-view's own address (Manage tab, sub-view path "campaigns"; plan 18.3). */
export const CAMPAIGNS_BASE = '/lms/manage/campaigns';

export function campaignHref(campaignId: string): string {
  return `${CAMPAIGNS_BASE}/${encodeURIComponent(campaignId)}`;
}

/** 5.4 lms_campaigns.kind in words: who sent it and why. */
export const CAMPAIGN_KIND_LABELS: Record<CampaignKind, string> = {
  manual: 'Assigned by hand',
  scenario: 'Scenario refresher',
  sop_update: 'SOP update',
  retrain: 'Retrain (new version)',
  acknowledge: 'Acknowledge (new version)',
};

const KIND_TONES: Record<CampaignKind, PillTone> = {
  manual: 'primary',
  scenario: 'primary',
  sop_update: 'primary',
  retrain: 'amber',
  acknowledge: 'amber',
};

export function CampaignKindPill({ kind }: { kind: CampaignKind }) {
  return <Pill tone={KIND_TONES[kind] ?? 'gray'}>{CAMPAIGN_KIND_LABELS[kind] ?? kind}</Pill>;
}

/**
 * The state of a recipient's assignment in words and colour (the 8.4
 * palette: open primary, overdue red, completed green, waived and cancelled
 * gray), judged at `nowMs`.
 */
export function assignmentState(
  assignment: CampaignRecipient['assignment'],
  nowMs: number
): { label: string; tone: PillTone; status: AssignmentStatus | 'overdue' | 'none' } {
  if (!assignment) return { label: 'No assignment', tone: 'gray', status: 'none' };
  switch (assignment.status) {
    case 'completed':
      return { label: `Completed ${formatDay(assignment.completed_at)}`, tone: 'green', status: 'completed' };
    case 'waived':
      return { label: 'Waived', tone: 'gray', status: 'waived' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'gray', status: 'cancelled' };
    default: {
      const due = Date.parse(assignment.due_at);
      if (!Number.isNaN(due) && due < nowMs) return { label: `Overdue since ${formatDay(assignment.due_at)}`, tone: 'red', status: 'overdue' };
      return { label: `Open, due ${formatDay(assignment.due_at)}`, tone: 'primary', status: 'open' };
    }
  }
}

/** A campaign's audience as the admin chose it, in a line (5.4; hidden people counted, 18.21). */
export function audienceSummary(campaign: CampaignListItem, siteNames: Record<string, string>, groupNames: Record<string, string>): string[] {
  const a = campaign.audience;
  if (!a) return [];
  const out: string[] = [];
  const sites = a.everyone_at_sites ?? [];
  if (sites.length > 0) out.push(`Everyone at ${sites.map((id) => siteNames[id] ?? 'a site outside your list').join(', ')}`);
  const roles = a.platform_roles ?? [];
  if (roles.length > 0) out.push(`Roles at the sending site: ${roles.map((r) => roleLabel(r)).join(', ')}`);
  const groups = a.group_ids ?? [];
  if (groups.length > 0) {
    const named = groups.map((id) => groupNames[id]).filter(Boolean);
    out.push(named.length === groups.length ? `Groups: ${named.join(', ')}` : `${groups.length.toLocaleString()} ${groups.length === 1 ? 'group' : 'groups'}`);
  }
  const people = (a.user_ids ?? []).length;
  const hidden = campaign.audience_hidden_user_count ?? 0;
  if (people + hidden > 0) {
    out.push(
      hidden > 0
        ? `${(people + hidden).toLocaleString()} named people (${hidden.toLocaleString()} at sites you do not reach)`
        : `${people.toLocaleString()} named ${people === 1 ? 'person' : 'people'}`
    );
  }
  return out;
}
